import { useField } from 'informed';
import { FC, KeyboardEvent } from 'react';
import { InformedFieldState } from '@common/interfaces';
import css from './styles.module.scss';
import { formatToMoneyString } from '@common/utils';

export interface INumberField {
  allowDecimal?: boolean;
  disabled?: boolean;
  maxLength?: number;
  name: string;
  skipFormatting?: boolean;
  variant?: 'light' | 'dark';
  placeholder?: string;
}

const NumberField: FC<INumberField> = ({
  allowDecimal = false,
  disabled,
  maxLength,
  name,
  skipFormatting = false,
  variant = 'light',
  placeholder,
}) => {
  const { fieldState, fieldApi, render, ref } = useField({ name });

  const { value, error, showError } = fieldState as InformedFieldState<string>;

  const { setValue, setTouched } = fieldApi;

  const validate = (e: KeyboardEvent<HTMLInputElement>, maxValue = 12) => {
    const length = (e.target as HTMLInputElement).value.length;
    const isMaxLength = length >= maxValue;
    const isAllowedKeys =
      (allowDecimal && e.key === '.') ||
      e.key === 'Backspace' ||
      e.key === 'Tab' ||
      (e.key === '-' && (e.currentTarget.selectionStart ?? 0) === 0) ||
      (e.shiftKey && e.key === 'Tab') ||
      (e.ctrlKey && e.key === 'v') ||
      (e.ctrlKey && e.key === 'c') ||
      (e.ctrlKey && e.key === 'a');
    const isNoDigit = !/^\d$/.test(e.key);
    if (!isAllowedKeys && (isNoDigit || isMaxLength)) {
      e.preventDefault();
    }
  };

  const format = (value: string) => {
    if (!value) return value;

    if (allowDecimal) {
      return formatToMoneyString(value);
    } else {
      return new Intl.NumberFormat('en-US').format(parseFloat(value.replace(/,/g, ''))).trim();
    }
  };

  return render(
    <div className={`${css.field} ${css[variant]}`}>
      <input
        ref={ref}
        value={value ?? ''}
        type='text'
        placeholder={placeholder}
        className={`${css.input} ${showError ? css.inputError : ''}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={(e) => {
          setTouched(true);
          if (!skipFormatting) setValue(format(e.target.value));
        }}
        disabled={disabled}
        onKeyDown={(e) => validate(e, maxLength)}
      />
      {showError ? <div className={css.error}>{error}</div> : null}
    </div>
  );
};

export default NumberField;
